// =============================================================================
// app/settings/appearance.tsx — theme / font size / display (bare-RN port).
//
// MIGRATION (P3 settings): @expo/vector-icons -> components/ui/Icon; the inline
// expo-router <Stack.Screen options> headers are dropped (SettingsStack owns the
// title). All API/business logic is unchanged.
//
// APPLE-ALIGNED PASS: full-bleed ParticleBackground replaces the flat lavender
// fill (mounted first, under the stack header per skill §12 — this is a nested
// settings list that keeps the stack header, so the aurora just shows in the
// body). Sections regrouped onto uniform GlassCards with title+subtitle headers,
// icon chips, hairline dividers, and tightened large-title/section typography
// (§15). All API/business logic (GET/PUT /settings/appearance) is unchanged.
// =============================================================================
import { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Switch,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '../../components/ui/Icon';
import { api } from '../../services/api';
import { colors, spacing, radius, fonts, typographyScale, getLayout } from '../../constants/theme';
import { GlassCard } from '../../components/glass';
import ParticleBackground from '../../components/ui/ParticleBackground';

type ThemeMode = 'light' | 'dark' | 'system';

// v1 is LIGHT-only (brand). Dark/System are gated "Soon" so the selector is
// honest — there is no runtime theme provider yet, so a live toggle would be a
// no-op. Light is always the applied theme.
const THEMES: { value: ThemeMode; icon: string; label: string; soon?: boolean }[] = [
  { value: 'light', icon: 'sunny-outline', label: 'Light' },
  { value: 'dark', icon: 'moon-outline', label: 'Dark', soon: true },
  { value: 'system', icon: 'phone-portrait-outline', label: 'System', soon: true },
];

type FontSize = 'small' | 'medium' | 'large';

const FONT_SIZES: { label: string; value: FontSize; px: number }[] = [
  { label: 'Small', value: 'small', px: 13 },
  { label: 'Medium', value: 'medium', px: 15 },
  { label: 'Large', value: 'large', px: 18 },
];

interface AppearancePrefs {
  theme: ThemeMode;
  fontSize: FontSize;
  compactMode: boolean;
  reduceMotion: boolean;
}

export default function AppearanceScreen() {
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const } : undefined;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [theme, setTheme] = useState<ThemeMode>('light');
  const [fontSize, setFontSize] = useState<FontSize>('medium');
  const [compactMode, setCompactMode] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  const fetchPreferences = useCallback(async () => {
    setLoading(true);
    const res = await api<AppearancePrefs>('/settings/appearance');
    if (res.success && res.data) {
      setTheme(res.data.theme || 'light');
      setFontSize(
        FONT_SIZES.some((f) => f.value === res.data!.fontSize)
          ? res.data.fontSize
          : 'medium'
      );
      setCompactMode(!!res.data.compactMode);
      setReduceMotion(!!res.data.reduceMotion);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  // Optimistic save: the UI flips immediately; on failure `revert` restores the
  // previous value so the screen never shows a state the server didn't accept.
  async function saveField(key: string, value: any, revert: () => void) {
    setSaving(true);
    // Backend defines PUT /settings/appearance (no PATCH) — using PATCH 404s.
    const res = await api('/settings/appearance', {
      method: 'PUT',
      body: JSON.stringify({ [key]: value }),
    });
    setSaving(false);
    if (!res.success) {
      revert();
      Alert.alert('Error', res.error || 'Failed to save preference.');
    }
  }

  function handleThemeSelect(value: ThemeMode) {
    const prev = theme;
    setTheme(value);
    saveField('theme', value, () => setTheme(prev));
  }

  function handleFontSizeSelect(value: FontSize) {
    const prev = fontSize;
    setFontSize(value);
    saveField('fontSize', value, () => setFontSize(prev));
  }

  function handleCompactToggle(value: boolean) {
    const prev = compactMode;
    setCompactMode(value);
    saveField('compactMode', value, () => setCompactMode(prev));
  }

  function handleAnimationsToggle(value: boolean) {
    const prev = reduceMotion;
    setReduceMotion(value);
    saveField('reduceMotion', value, () => setReduceMotion(prev));
  }

  const safeFontIndex = Math.max(0, FONT_SIZES.findIndex((f) => f.value === fontSize));
  const currentFont = FONT_SIZES[safeFontIndex] || FONT_SIZES[1];

  if (loading) {
    return (
      <View style={styles.screen}>
        <ParticleBackground theme="light" />
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <ParticleBackground theme="light" />
      <ScrollView
        contentContainerStyle={[styles.scrollContent, containerStyle]}
        showsVerticalScrollIndicator={false}
      >
        {/* Theme */}
        <View style={styles.group}>
          <View style={styles.groupHeader}>
            <Text style={styles.sectionTitle}>Theme</Text>
            <Text style={styles.sectionSubtitle}>How CRYB looks on this device</Text>
          </View>
          <GlassCard>
            <View style={styles.themeRow}>
              {THEMES.map((t) => {
                // Light is always the applied theme in v1; Dark/System are "Soon".
                const active = t.value === 'light';
                const disabled = !!t.soon;
                return (
                  <TouchableOpacity
                    key={t.value}
                    style={[styles.themeOption, active && styles.themeOptionActive, disabled && { opacity: 0.5 }]}
                    onPress={() => { if (!disabled) handleThemeSelect(t.value); }}
                    disabled={disabled}
                    activeOpacity={0.7}
                    accessibilityRole="button"
                    accessibilityLabel={disabled ? `${t.label} theme, coming soon` : `${t.label} theme`}
                    accessibilityState={{ selected: active, disabled }}
                  >
                    <Ionicons
                      name={t.icon as any}
                      size={24}
                      color={active ? colors.primary : colors.muted}
                    />
                    <Text style={[styles.themeLabel, active && styles.themeLabelActive]}>
                      {t.label}
                    </Text>
                    {disabled && (
                      <View style={styles.themeSoonPill}>
                        <Text style={styles.themeSoonPillText}>Soon</Text>
                      </View>
                    )}
                  </TouchableOpacity>
                );
              })}
            </View>
          </GlassCard>
        </View>

        {/* Font Size */}
        <View style={styles.group}>
          <View style={styles.groupHeader}>
            <Text style={styles.sectionTitle}>Font Size</Text>
            <Text style={styles.sectionSubtitle}>Text scale across the app</Text>
          </View>
          <GlassCard>
            <Text style={[styles.previewText, { fontSize: currentFont.px }]}>
              Preview text at {currentFont.label} size
            </Text>
            <View style={styles.sliderRow}>
              <Text style={styles.sliderLabel}>A</Text>
              <View style={styles.sliderTrack}>
                {FONT_SIZES.map((fs, idx) => (
                  <TouchableOpacity
                    key={fs.value}
                    style={[
                      styles.sliderDot,
                      idx === safeFontIndex && styles.sliderDotActive,
                    ]}
                    onPress={() => handleFontSizeSelect(fs.value)}
                    activeOpacity={0.7}
                    accessibilityRole="button"
                    accessibilityLabel={`${fs.label} font size`}
                    accessibilityState={{ selected: idx === safeFontIndex }}
                    hitSlop={{ top: 10, bottom: 10, left: 6, right: 6 }}
                  >
                    {idx === safeFontIndex && <View style={styles.sliderDotInner} />}
                  </TouchableOpacity>
                ))}
                <View style={styles.sliderLine} />
              </View>
              <Text style={styles.sliderLabelLg}>A</Text>
            </View>
            <View style={styles.sizeLabels}>
              {FONT_SIZES.map((fs, idx) => (
                <Text
                  key={fs.value}
                  style={[styles.sizeLabel, idx === safeFontIndex && styles.sizeLabelActive]}
                >
                  {fs.label}
                </Text>
              ))}
            </View>
          </GlassCard>
        </View>

        {/* Display Options */}
        <View style={styles.group}>
          <View style={styles.groupHeader}>
            <Text style={styles.sectionTitle}>Display</Text>
            <Text style={styles.sectionSubtitle}>Density and motion</Text>
          </View>
          <GlassCard padding={0}>
            <View style={[styles.row, styles.rowBorder]}>
              <View style={styles.rowLeft}>
                <View style={styles.iconWrap}>
                  <Ionicons name="grid-outline" size={20} color={colors.primary} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.label} numberOfLines={1}>Compact Mode</Text>
                  <Text style={styles.desc} numberOfLines={2}>
                    Show more content with less spacing
                  </Text>
                </View>
              </View>
              <Switch
                value={!!compactMode}
                onValueChange={handleCompactToggle}
                trackColor={{ false: colors.faint, true: colors.primary + '60' }}
                thumbColor={compactMode ? colors.primary : colors.card}
                accessibilityLabel="Compact mode"
              />
            </View>

            <View style={styles.row}>
              <View style={styles.rowLeft}>
                <View style={styles.iconWrap}>
                  <Ionicons name="sparkles-outline" size={20} color={colors.primary} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.label} numberOfLines={1}>Reduce Animations</Text>
                  <Text style={styles.desc} numberOfLines={2}>
                    Minimize motion for accessibility
                  </Text>
                </View>
              </View>
              <Switch
                value={!!reduceMotion}
                onValueChange={handleAnimationsToggle}
                trackColor={{ false: colors.faint, true: colors.primary + '60' }}
                thumbColor={reduceMotion ? colors.primary : colors.card}
                accessibilityLabel="Reduce animations"
              />
            </View>
          </GlassCard>
        </View>

        {saving && (
          <View style={styles.savingOverlay}>
            <ActivityIndicator size="small" color={colors.primary} />
            <Text style={styles.savingText}>Saving...</Text>
          </View>
        )}

        <View style={{ height: spacing.xxxl }} />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  loadingWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollContent: {
    padding: spacing.lg,
  },
  group: {
    marginBottom: spacing.xl,
  },
  groupHeader: {
    marginBottom: spacing.sm,
    marginLeft: spacing.xs,
  },
  sectionTitle: {
    ...typographyScale.caption,
    color: colors.muted,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    ...fonts.bold,
  },
  sectionSubtitle: {
    fontSize: 12,
    color: colors.faint,
    marginTop: 2,
    ...fonts.regular,
  },
  themeRow: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  themeOption: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.lg,
    borderRadius: radius.md,
    backgroundColor: colors.primary + '08',
    borderWidth: 1.5,
    borderColor: 'transparent',
    gap: spacing.sm,
    position: 'relative',
    overflow: 'hidden',
  },
  themeSoonPill: {
    position: 'absolute',
    top: 6,
    right: 6,
    backgroundColor: colors.primary + '1A',
    borderRadius: radius.full,
    paddingHorizontal: 6,
    paddingVertical: 1,
  },
  themeSoonPillText: { fontSize: 9, color: colors.primary, fontWeight: '700', letterSpacing: 0.3 },
  themeOptionActive: {
    borderColor: colors.primary,
    backgroundColor: colors.primary + '14',
  },
  themeLabel: {
    fontSize: 13,
    color: colors.muted,
    letterSpacing: 0.1,
    ...fonts.medium,
  },
  themeLabelActive: {
    color: colors.primary,
    ...fonts.semibold,
  },
  previewText: {
    color: colors.ink,
    textAlign: 'center',
    marginBottom: spacing.lg,
    ...fonts.regular,
  },
  sliderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  sliderLabel: {
    fontSize: 13,
    color: colors.muted,
    ...fonts.medium,
  },
  sliderLabelLg: {
    fontSize: 19,
    color: colors.muted,
    ...fonts.medium,
  },
  sliderTrack: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 30,
    position: 'relative',
  },
  sliderLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 2,
    backgroundColor: colors.faint,
    zIndex: -1,
  },
  sliderDot: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colors.white,
    borderWidth: 2,
    borderColor: colors.faint,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  sliderDotActive: {
    borderColor: colors.primary,
    backgroundColor: colors.white,
  },
  sliderDotInner: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: colors.primary,
  },
  sizeLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: spacing.sm,
    paddingHorizontal: spacing.xl,
  },
  sizeLabel: {
    fontSize: 11,
    color: colors.faint,
    ...fonts.regular,
  },
  sizeLabelActive: {
    color: colors.primary,
    ...fonts.semibold,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    gap: spacing.md,
  },
  rowBorder: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.cardBorder,
  },
  rowLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    flex: 1,
  },
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: radius.sm,
    backgroundColor: colors.primary + '10',
    justifyContent: 'center',
    alignItems: 'center',
  },
  label: {
    ...typographyScale.body,
    color: colors.ink,
    ...fonts.medium,
  },
  desc: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 2,
    ...fonts.regular,
  },
  savingOverlay: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
  savingText: {
    fontSize: 13,
    color: colors.muted,
    ...fonts.medium,
  },
});
